import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { ArrowRightIcon, classNames, getTestIdAttribute } from '@groma-dev/blueprint';
import { Avatar, NavItem, parseUserInitials } from '@groma-dev/navigation';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { useDataStore } from '../../../providers/StoresProvider';
import { HeaderMenu } from '../Header';
import { Menu } from '../menu/Menu';
import { AppRoutes } from '../../../../core/routes';
import styles from './MobileMenu.module.scss';

export interface MobileMenuProps {
  open: boolean;
  submenuOpen: boolean;
  toggleSubmenu: (value: boolean) => void;
  signOutUser?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

export const MobileMenu = observer((props: MobileMenuProps) => {
  const { open, submenuOpen, toggleSubmenu, signOutUser } = props;
  const dataStore = useDataStore();
  const { appState } = dataStore;
  const [submenuSection, setSubmenuSection] = useState<HeaderMenu | undefined>();

  const handleWindowScroll = () => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.removeProperty('overflow');
    }
  };

  const handleItemClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    toggleSubmenu(true);
    setSubmenuSection(e.currentTarget.name as HeaderMenu);
  };

  useEffect(() => {
    handleWindowScroll();

    if (!open) {
      toggleSubmenu(false);
      setSubmenuSection(undefined);
    }
  }, [open]);

  return (
    <>
      <nav
        aria-label="menu"
        className={classNames(styles.menuContainer, open && styles.open, submenuOpen && styles.hidden)}>
        <ul className={classNames(styles.menuList)}>
          {!appState.nonREITAccount && (
            <li {...getTestIdAttribute('mobileNavItem')}>
              <NavItem
                to={AppRoutes.Overview}
                as={Link}
                className={classNames(styles.menuItem)}
                testId="overviewMobileNavItem">
                <FormattedMessage
                  id="header.mobileMenu.overview"
                  defaultMessage="Overview"
                  description="Mobile Menu overview nav item"
                />
                <ArrowRightIcon />
              </NavItem>
            </li>
          )}
          {!appState.nonREITAccount && (
            <li {...getTestIdAttribute('mobileNavItem')}>
              <NavItem
                to={AppRoutes.Invest}
                as={Link}
                className={classNames(styles.menuItem)}
                testId="investMobileNavItem">
                <FormattedMessage
                  id="header.mobileMenu.Invest"
                  defaultMessage="Invest"
                  description="Mobile Menu invest nav item"
                />
                <ArrowRightIcon />
              </NavItem>
            </li>
          )}
          <li {...getTestIdAttribute('mobileNavItem')}>
            <NavItem
              to={AppRoutes.Documents}
              as={Link}
              className={classNames(styles.menuItem)}
              testId="documentsMobileNavItem">
              <FormattedMessage
                id="header.mobileMenu.documents"
                defaultMessage="Documents"
                description="Mobile Menu documents nav item"
              />
              <ArrowRightIcon />
            </NavItem>
          </li>
          <li {...getTestIdAttribute('mobileNavItem')}>
            <NavItem
              to={AppRoutes.Settings}
              as={Link}
              className={classNames(styles.menuItem)}
              testId="settingsMobileNavItem">
              <FormattedMessage
                id="header.mobileMenu.settings"
                defaultMessage="Account Settings"
                description="Mobile Menu settings nav item"
              />
              <ArrowRightIcon />
            </NavItem>
          </li>
          <li {...getTestIdAttribute('mobileNavItem')}>
            <NavItem to={AppRoutes.Help} as={Link} className={classNames(styles.menuItem)} testId="helpMobileNavItem">
              <FormattedMessage
                id="header.mobileMenu.help"
                defaultMessage="Help & Feedback"
                description="Mobile Menu help nav item"
              />
              <ArrowRightIcon />
            </NavItem>
          </li>
          <li>
            {dataStore.userState.user ? (
              <NavItem
                as="button"
                className={classNames(styles.menuItem)}
                onClick={handleItemClick}
                testId="mobileAccountButton">
                <span className={classNames(styles.profileContent)}>
                  <Avatar>{parseUserInitials(dataStore.userState.user)}</Avatar>
                  <FormattedMessage
                    id="header.mobileMenu.profile"
                    defaultMessage="Profile"
                    description="Mobile Menu profile nav item"
                  />
                </span>
                <ArrowRightIcon />
              </NavItem>
            ) : (
              <NavItem
                as="button"
                className={classNames(styles.menuItem)}
                onClick={handleItemClick}
                testId="signInMobile">
                <FormattedMessage
                  id="header.mobileMenu.signin"
                  defaultMessage="Sign In"
                  description="Mobile Menu sign in nav item"
                />
                <ArrowRightIcon />
              </NavItem>
            )}
          </li>
        </ul>
      </nav>
      <nav aria-label="submenu" className={classNames(styles.submenuContainer, submenuOpen && styles.open)}>
        <Menu section={submenuSection} signOutUser={signOutUser} />
      </nav>
    </>
  );
});
